import { apiFetch } from "./client";

export interface SupportTicketSummary {
  id: string;
  subject: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: string;
  source: string;
  requesterName: string | null;
  companyId?: string;
  companyName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupportTicketComment {
  id: string;
  authorType: "requester" | "platform_team" | "scout";
  authorName: string | null;
  content: string;
  internal?: boolean;
  createdAt: string;
}

export interface SupportTicketDetail {
  ticket: SupportTicketSummary & {
    description: string;
    notifyChannel?: string;
    context?: Record<string, unknown> | null;
  };
  comments: SupportTicketComment[];
}

export interface AvailableChannels {
  inApp: true;
  teams: boolean;
  email: boolean;
  emailAddress: string | null;
}

export const supportTicketsApi = {
  // Tenant-facing
  list: (companyId: string) =>
    apiFetch<{ tickets: SupportTicketSummary[] }>(`/companies/${companyId}/support-tickets`),
  channels: (companyId: string) =>
    apiFetch<AvailableChannels>(`/companies/${companyId}/support-tickets/channels`),
  get: (companyId: string, ticketId: string) =>
    apiFetch<SupportTicketDetail>(`/companies/${companyId}/support-tickets/${ticketId}`),
  create: (companyId: string, data: { subject: string; description: string; priority?: string; notifyChannel?: string; context?: Record<string, unknown> }) =>
    apiFetch<SupportTicketSummary>(`/companies/${companyId}/support-tickets`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  addComment: (companyId: string, ticketId: string, content: string) =>
    apiFetch<SupportTicketComment>(`/companies/${companyId}/support-tickets/${ticketId}/comments`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  // Platform-team (instance admin)
  adminList: (status?: string) =>
    apiFetch<{ tickets: SupportTicketSummary[] }>(
      `/admin/support-tickets${status ? `?status=${status}` : ""}`,
    ),
  adminGet: (ticketId: string) =>
    apiFetch<SupportTicketDetail>(`/admin/support-tickets/${ticketId}`),
  adminSetStatus: (ticketId: string, status: string) =>
    apiFetch<SupportTicketSummary>(`/admin/support-tickets/${ticketId}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  adminAddComment: (ticketId: string, content: string, internal: boolean) =>
    apiFetch<SupportTicketComment>(`/admin/support-tickets/${ticketId}/comments`, {
      method: "POST",
      body: JSON.stringify({ content, internal }),
    }),
};

export interface UserNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read: boolean;
  createdAt: string;
}

export const userNotificationsApi = {
  list: () =>
    apiFetch<{ notifications: UserNotification[]; unreadCount: number }>("/notifications"),
  markRead: (id: string) => apiFetch<{ ok: boolean }>(`/notifications/${id}/read`, { method: "POST" }),
  markAllRead: () => apiFetch<{ ok: boolean }>("/notifications/read-all", { method: "POST" }),
};
